/**
 * Consultas e gravações do caixa pelo TanStack Query.
 *
 * Papel: aqui ficam `CHAVE_FINANCAS`, as seis consultas que a tela lê — o
 * caixa, o Fechamento, a listagem, o plano de contas, os favorecidos e os
 * padrões — e as mutações que gravam.
 *
 * Como usar: toda chave começa por `CHAVE_FINANCAS`, e **toda gravação
 * invalida `CHAVE_FINANCAS` inteira**. Um lançamento novo muda o Saldo Atual, o
 * Fechamento, a listagem, o `emUso` de uma categoria e o de um favorecido — e
 * pode acrescentar categoria ao plano e favorecido à lista; invalidar só um
 * ramo deixaria um desses números velho na tela até recarregar. Compras
 * invalida a mesma chave depois de um pedido virar comprado, e uma consulta com
 * outro prefixo mostraria o caixa sem a despesa do pedido.
 *
 * A listagem guarda o resultado anterior enquanto o novo não chega
 * (`keepPreviousData`), como a origem, que redesenhava a tabela já pronta a
 * cada tecla; sem isso, a tabela piscaria "Carregando…" a cada filtro.
 */
import {
  type ConsultaDoFechamento,
  type FiltroDeLancamentos,
  type LoteDeLancamentos,
  type NovaCategoria,
  type NovoFavorecido,
  type PerfilDoFavorecido,
  type ReordenarLancamento,
  type SalvarLancamento,
} from '@concierge/shared';
import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  aplicarLote,
  atualizarLancamento,
  buscarCaixa,
  buscarFavorecidos,
  buscarFechamento,
  buscarLancamentos,
  buscarPadroesDeFavorecido,
  buscarPlanoDeContas,
  criarCategoria,
  criarFavorecido,
  criarLancamento,
  criarSubcategorias,
  excluirCategoria,
  excluirFavorecido,
  excluirLancamento,
  excluirSubcategoria,
  mudarCorDaCategoria,
  reordenarLancamento,
  salvarPerfilDoFavorecido,
} from '../api/financas';

/** Prefixo de todas as chaves de cache deste módulo. */
export const CHAVE_FINANCAS = ['financas'] as const;

/**
 * Lê a casa do caixa, o mês-base e o Saldo Atual.
 *
 * @returns A consulta do caixa.
 */
export function useCaixa() {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'caixa'],
    queryFn: ({ signal }) => buscarCaixa(signal),
  });
}

/** O que as abas sabem da casa do caixa. */
export interface CasaDoCaixa {
  /**
   * A casa do caixa; nula enquanto o caixa não chegou e, com `situacao`
   * `pronta`, quando nenhuma casa tem caixa.
   */
  residenciaId: string | null;
  situacao: 'carregando' | 'falhou' | 'pronta';
  /** O Saldo Atual; nulo enquanto o caixa não chegou. */
  saldoAtual: number | null;
  /** Pede o caixa de novo, para o "Tentar de novo" da falha. */
  tentarDeNovo: () => void;
}

/**
 * A casa do caixa e o Saldo Atual, como as abas os leem.
 *
 * Cada aba chama este gancho por conta própria, e o TanStack Query faz uma
 * consulta só para todas. Enquanto o caixa não chega, as tabelas dizem
 * "Carregando…"; se ele falhar, o "Tentar de novo" de qualquer tabela o pede de
 * novo, porque nenhuma outra consulta sai sem a casa.
 *
 * @returns A casa, a situação da consulta, o Saldo Atual e o pedido de novo.
 */
export function useCasaDoCaixa(): CasaDoCaixa {
  const caixa = useCaixa();
  let situacao: CasaDoCaixa['situacao'] = 'pronta';
  if (caixa.data === undefined) situacao = caixa.isError ? 'falhou' : 'carregando';
  return {
    residenciaId: caixa.data?.residencia?.id ?? null,
    situacao,
    saldoAtual: caixa.data?.saldoAtual ?? null,
    tentarDeNovo: () => void caixa.refetch(),
  };
}

/**
 * Lê o Fechamento do mês aberto.
 *
 * @param consulta - A casa do caixa e o mês aberto; nula enquanto a casa não
 *   chegou, o que deixa a consulta parada.
 * @returns A consulta do Fechamento.
 */
export function useFechamento(consulta: ConsultaDoFechamento | null) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'fechamento', consulta],
    queryFn:
      consulta === null ? skipToken : ({ signal }) => buscarFechamento(consulta, signal),
  });
}

/**
 * Lê as linhas da tabela de lançamentos.
 *
 * @param filtro - A casa, o mês aberto e os filtros; nulo enquanto a casa não
 *   chegou.
 * @returns A consulta da listagem, com o resultado anterior à vista enquanto o
 *   novo não chega.
 */
export function useLancamentos(filtro: FiltroDeLancamentos | null) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'lancamentos', filtro],
    queryFn: filtro === null ? skipToken : ({ signal }) => buscarLancamentos(filtro, signal),
    placeholderData: keepPreviousData,
  });
}

/**
 * Lê o plano de contas.
 *
 * @returns A consulta do plano, em ordem alfabética.
 */
export function usePlanoDeContas() {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'plano'],
    queryFn: ({ signal }) => buscarPlanoDeContas(signal),
  });
}

/**
 * Lê os favorecidos da casa do caixa.
 *
 * @param residenciaId - A casa do caixa; nula deixa a consulta parada.
 * @returns A consulta dos favorecidos.
 */
export function useFavorecidos(residenciaId: string | null) {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'favorecidos', residenciaId],
    queryFn:
      residenciaId === null
        ? skipToken
        : ({ signal }) => buscarFavorecidos(residenciaId, signal),
  });
}

/**
 * Lê os padrões de favorecido.
 *
 * @returns A consulta dos padrões.
 */
export function usePadroesDeFavorecido() {
  return useQuery({
    queryKey: [...CHAVE_FINANCAS, 'padroes'],
    queryFn: ({ signal }) => buscarPadroesDeFavorecido(signal),
  });
}

/**
 * Devolve a função que invalida todo o caixa depois de uma gravação.
 *
 * @returns A função, para o `onSettled` das mutações: a recusa da API também
 *   invalida, porque costuma dizer que outra pessoa mudou o caixa — pagou ou
 *   excluiu o lançamento — e a tela mostra o que já não existe. A promessa é
 *   devolvida, e o `mutateAsync` só termina depois da nova busca.
 */
function useInvalidarCaixa() {
  const clienteDeConsultas = useQueryClient();
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_FINANCAS });
}

/** O que a gravação do modal recebe: o lançamento editado, ou nulo para criar. */
interface SalvarLancamentoVariaveis {
  id: string | null;
  corpo: SalvarLancamento;
}

/**
 * Cria ou edita pelo modal, conforme venha identificador.
 *
 * @returns A mutação, que devolve as linhas gravadas.
 */
export function useSalvarLancamento() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: ({ id, corpo }: SalvarLancamentoVariaveis) =>
      id === null ? criarLancamento(corpo) : atualizarLancamento(id, corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Exclui um lançamento.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirLancamento() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (id: string) => excluirLancamento(id),
    onSettled: () => invalidar(),
  });
}

/**
 * Aplica uma ação de lote.
 *
 * @returns A mutação, que recebe a ação e os identificadores.
 */
export function useLoteDeLancamentos() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (lote: LoteDeLancamentos) => aplicarLote(lote),
    onSettled: () => invalidar(),
  });
}

/**
 * Move um lançamento dentro do mês.
 *
 * @returns A mutação, que recebe quem foi solto e onde.
 */
export function useReordenarLancamento() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: ReordenarLancamento) => reordenarLancamento(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Cria uma categoria.
 *
 * @returns A mutação, que recebe o nome, a cor e as subcategorias.
 */
export function useCriarCategoria() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: NovaCategoria) => criarCategoria(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Grava a cor de uma categoria.
 *
 * @returns A mutação, que recebe a categoria e a cor.
 */
export function useMudarCorDaCategoria() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: ({ id, cor }: { id: string; cor: string }) => mudarCorDaCategoria(id, cor),
    onSettled: () => invalidar(),
  });
}

/**
 * Exclui uma categoria.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirCategoria() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (id: string) => excluirCategoria(id),
    onSettled: () => invalidar(),
  });
}

/**
 * Acrescenta subcategorias a uma categoria.
 *
 * @returns A mutação, que recebe a categoria e os nomes.
 */
export function useCriarSubcategorias() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: ({ categoriaId, nomes }: { categoriaId: string; nomes: readonly string[] }) =>
      criarSubcategorias(categoriaId, nomes),
    onSettled: () => invalidar(),
  });
}

/**
 * Exclui uma subcategoria.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirSubcategoria() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (id: string) => excluirSubcategoria(id),
    onSettled: () => invalidar(),
  });
}

/**
 * Cadastra um favorecido só com o nome.
 *
 * @returns A mutação, que devolve o favorecido criado.
 */
export function useCriarFavorecido() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (corpo: NovoFavorecido) => criarFavorecido(corpo),
    onSettled: () => invalidar(),
  });
}

/**
 * Grava o perfil de um favorecido.
 *
 * @returns A mutação, que devolve o favorecido como ficou gravado.
 */
export function useSalvarPerfil() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: ({ id, perfil }: { id: string; perfil: PerfilDoFavorecido }) =>
      salvarPerfilDoFavorecido(id, perfil),
    onSettled: () => invalidar(),
  });
}

/**
 * Exclui um favorecido.
 *
 * @returns A mutação, que recebe o identificador.
 */
export function useExcluirFavorecido() {
  const invalidar = useInvalidarCaixa();
  return useMutation({
    mutationFn: (id: string) => excluirFavorecido(id),
    onSettled: () => invalidar(),
  });
}
